import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { useEffect, useState } from 'react';
import { LoadingBlockFullPage } from '../../../components/loading/loading-block-full-page.tsx';
import { TitleWithCopyButton } from '../../../components/ui/buttons/copy-button.tsx';
import { DrawerContainer, DrawerContent, DrawerHeader } from '../../../components/ui/drawer.tsx';
import { TabsHeader } from '../../../components/ui/tabs-header.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { Drawer404 } from '../../drawers/drawer-404.tsx';
import { OrderStatusBadge } from '../../statuses/components/order-status-badge.tsx';
import { QuoteOrderActionDropdownMenu } from '../components/quote-order-action-dropdown-menu.tsx';
import { QuoteOrderAttachmentsTab } from '../components/quote-order-attachments-tab.tsx';
import { QuoteOrderDetailTab } from '../components/quote-order-detail-tab.tsx';
import { useDetailQuoteOrder } from '../hooks/use-detail-quote-order.tsx';

enum PageTabs {
    DETAILS = 'details',
    ATTACHMENTS = 'attachments',
}

type Props = {
    className?: string;
    onBack?: () => void;
    onClose?: () => void;
    type: SrSalesOrderType;
    transactionId: string;
};

export function QuoteOrderDetailPage({ onBack, onClose, className, transactionId, type }: Props) {
    const { t } = useLocale();
    const [selectedTab, setSelectedTab] = useState<PageTabs>(PageTabs.DETAILS);

    const { canViewSalesOrderDocuments } = useAppConfig();

    const { data, isLoading } = useDetailQuoteOrder({ transactionId });
    const sendEvent = useAnalytics();
    const customerId = data?.header.customer_id;

    useEffect(() => {
        if (customerId) {
            sendEvent('quote_order_detail_viewed', {
                customer_id: customerId,
                quote_order_type: type,
                transaction_id: transactionId,
            });
        }
    }, [customerId, sendEvent, transactionId, type]);

    if (isLoading) {
        return <LoadingBlockFullPage />;
    }

    if (!data) {
        return <Drawer404 onBack={onBack} onClose={onClose} />;
    }

    const { header } = data;

    const title = `${
        type === SrSalesOrderType.ORDER
            ? t('order', { ns: 'transaction-details' })
            : t('quote', { ns: 'transaction-details' })
    } ${header.display_identifier}`;

    return (
        <DrawerContainer className={className}>
            <div className="flex flex-col gap-3 bg-white">
                <DrawerHeader
                    className="border-b-0 pb-0"
                    onBack={onBack}
                    onClose={onClose}
                    title={
                        header.display_identifier ? (
                            <TitleWithCopyButton text={title} textToCopy={header.display_identifier} />
                        ) : (
                            title
                        )
                    }
                    actions={
                        <QuoteOrderActionDropdownMenu
                            customerId={header.customer_id}
                            type={type}
                            transactionId={transactionId}
                            onDelete={onClose ?? onBack}
                        />
                    }
                >
                    <OrderStatusBadge status={header.status} type={header.type} className="ml-0.5" />
                </DrawerHeader>
                <TabsHeader<PageTabs>
                    className="px-4"
                    selectedTabKey={selectedTab}
                    onTabChange={(value) => {
                        setSelectedTab(value);
                    }}
                    tabs={[PageTabs.DETAILS, ...(canViewSalesOrderDocuments ? [PageTabs.ATTACHMENTS] : [])]}
                    getTabLabel={(tab) => t(`tabs__${tab}`, { ns: 'transaction-details' })}
                />
            </div>
            <DrawerContent>
                {selectedTab === PageTabs.DETAILS && (
                    <QuoteOrderDetailTab data={data} type={type} isLoading={isLoading} />
                )}
                {selectedTab === PageTabs.ATTACHMENTS && (
                    <QuoteOrderAttachmentsTab transactionId={transactionId} />
                )}
            </DrawerContent>
        </DrawerContainer>
    );
}
